import { useState } from 'react';
import { getAPIBase } from './config';
import logoPHSchool from './assets/logo/logo_PHSchool.png';
import './RegisterPage.css';

const translations = {
  en: {
    kicker: 'Online Admissions Portal',
    title: 'Parent Account Registration',
    subtitle: 'Fill in your details below to create your official parent / guardian admissions account.',
    showcaseBadge: '1 Account for All Children',
    showcaseHeading: 'One Family Account for Every Education Level',
    showcaseDesc: 'Register and manage admissions for one or multiple children across all levels from a single unified parent desk.',
    levelsTitle: 'Available Education Levels',
    levels: [
      { code: 'PG', name: 'Preschool & Playgroup' },
      { code: 'TK', name: 'Kindergarten (TK)' },
      { code: 'SD', name: 'Primary School (SD)' },
      { code: 'SMP', name: 'Secondary School (SMP)' },
      { code: 'SMA', name: 'Senior High School (SMA)' },
    ],
    perks: [
      '1 parent account can enroll more than 1 child',
      'Select any education level (Preschool to High School)',
      'Monitor admissions process & results in real time',
    ],
    trustText: 'Official School Admissions Portal with End-to-End Data Encryption',
    fullNameLabel: 'Parent / Guardian Full Name',
    fullNamePlaceholder: 'Full name as per official ID',
    emailLabel: 'Email Address',
    emailPlaceholder: 'name@email.com (used for portal login)',
    phoneLabel: 'WhatsApp / Phone Number',
    phonePlaceholder: '081234567890 (active WhatsApp number)',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Min. 8 characters',
    confirmPasswordLabel: 'Confirm Password',
    confirmPasswordPlaceholder: 'Re-enter password',
    passwordHint: 'Minimum 8 characters with a mix of letters and numbers',
    registerButton: 'Create Parent Account',
    registeringButton: 'Creating Your Account...',
    loginPrompt: 'Already have a parent account?',
    loginLink: 'Sign In to Admissions Portal',
    backToHome: 'Back to Homepage',
    successKicker: 'Account Activated',
    successTitle: 'Parent Account Created Successfully!',
    successMessage: 'Your parent profile is active. Sign in now to choose education levels (PG, TK, SD, SMP, SMA) and enroll your children. You can enroll multiple children in this single account.',
    signInButton: 'Sign In to Admissions Portal',
    errorTitle: 'Unable to Register',
    passwordMismatch: 'Passwords do not match',
    passwordTooShort: 'Password must be at least 8 characters long',
    invalidEmail: 'Please enter a valid email address',
    invalidPhone: 'Phone number must be 10-13 digits (format: 08xxx)',
    requiredFields: 'Please fill in all required fields marked with an asterisk (*)',
  },
  id: {
    kicker: 'Portal Penerimaan Peserta Didik Baru (PPDB)',
    title: 'Registrasi Akun Orang Tua',
    subtitle: 'Lengkapi formulir di bawah ini untuk membuat akun resmi orang tua / wali murid.',
    showcaseBadge: '1 Akun untuk Seluruh Keluarga',
    showcaseHeading: 'Satu Akun Orang Tua untuk Semua Jenjang',
    showcaseDesc: 'Kemudahan mendaftarkan dan memantau penerimaan seluruh anak Anda di berbagai jenjang cukup dari satu portal terpadu.',
    levelsTitle: 'Jenjang Pendidikan yang Tersedia',
    levels: [
      { code: 'PG', name: 'Preschool & Playgroup' },
      { code: 'TK', name: 'Taman Kanak-Kanak (TK)' },
      { code: 'SD', name: 'Sekolah Dasar (SD)' },
      { code: 'SMP', name: 'Sekolah Menengah Pertama (SMP)' },
      { code: 'SMA', name: 'Sekolah Menengah Atas (SMA)' },
    ],
    perks: [
      '1 akun orang tua dapat mendaftarkan lebih dari 1 anak',
      'Bebas memilih jenjang pendaftaran (PG, TK, SD, SMP, SMA)',
      'Pantau tahapan seleksi dan pengumuman secara transparan',
    ],
    trustText: 'Sistem Penerimaan Resmi Sekolah Terintegrasi & Terenkripsi Aman',
    fullNameLabel: 'Nama Lengkap Orang Tua / Wali',
    fullNamePlaceholder: 'Nama lengkap sesuai KTP',
    emailLabel: 'Alamat Email',
    emailPlaceholder: 'nama@email.com (untuk login & notifikasi)',
    phoneLabel: 'Nomor WhatsApp / Telepon',
    phonePlaceholder: '081234567890 (nomor WhatsApp aktif)',
    passwordLabel: 'Kata Sandi',
    passwordPlaceholder: 'Minimal 8 karakter',
    confirmPasswordLabel: 'Konfirmasi Kata Sandi',
    confirmPasswordPlaceholder: 'Masukkan kembali kata sandi',
    passwordHint: 'Minimal 8 karakter kombinasi huruf dan angka',
    registerButton: 'Buat Akun Orang Tua',
    registeringButton: 'Sedang Membuat Akun...',
    loginPrompt: 'Sudah memiliki akun orang tua?',
    loginLink: 'Masuk ke Portal Pendaftaran',
    backToHome: 'Kembali ke Beranda',
    successKicker: 'Pendaftaran Berhasil',
    successTitle: 'Akun Orang Tua Berhasil Dibuat!',
    successMessage: 'Akun orang tua Anda telah aktif. Silakan masuk (login) untuk memilih jenjang pendidikan anak Anda (Playgroup, TK, SD, SMP, SMA) dan melengkapi data pendaftaran. Anda dapat mendaftarkan lebih dari satu anak dalam akun ini.',
    signInButton: 'Masuk ke Portal Pendaftaran',
    errorTitle: 'Pendaftaran Belum Berhasil',
    passwordMismatch: 'Konfirmasi kata sandi tidak cocok',
    passwordTooShort: 'Kata sandi harus minimal 8 karakter',
    invalidEmail: 'Silakan masukkan alamat email yang valid',
    invalidPhone: 'Nomor telepon harus 10-13 digit (format Indonesia: 08xxx)',
    requiredFields: 'Silakan isi semua bidang yang bertanda bintang (*)',
  },
};

function RegisterPage({ language = 'id', onNavigate }) {
  const [currentLang, setCurrentLang] = useState(language || 'id');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const copy = translations[currentLang] || translations.id;

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const cleanedPhone = phone.replace(/\D/g, '');
    const phoneRegex = /^0\d{9,12}$/;
    return phoneRegex.test(cleanedPhone);
  };

  const sanitizePhone = (phone) => {
    return phone.replace(/\D/g, '');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === 'phone') {
      processedValue = value.replace(/\D/g, '');
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (
      !formData.fullName ||
      !formData.email ||
      !formData.phone ||
      !formData.password ||
      !formData.confirmPassword
    ) {
      setError(copy.requiredFields);
      return;
    }

    const fullName = formData.fullName.trim();
    if (fullName.length < 3) {
      setError(
        currentLang === 'id'
          ? 'Nama harus minimal 3 karakter'
          : 'Name must be at least 3 characters'
      );
      return;
    }

    if (!validateEmail(formData.email)) {
      setError(copy.invalidEmail);
      return;
    }

    if (!validatePhone(formData.phone)) {
      setError(copy.invalidPhone);
      return;
    }

    if (formData.password.length < 8) {
      setError(copy.passwordTooShort);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(copy.passwordMismatch);
      return;
    }

    setIsSubmitting(true);

    try {
      const apiBase = getAPIBase ? getAPIBase() : 'https://permataharapansecondary.sch.id/api';
      const response = await fetch(`${apiBase}/admissions/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          full_name: fullName,
          email: formData.email.toLowerCase().trim(),
          phone: sanitizePhone(formData.phone),
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (data.ok) {
        setSuccess(true);
        try {
          localStorage.setItem('login_prefill_email', formData.email.toLowerCase().trim());
        } catch (_) {}
      } else {
        setError(data.message || (currentLang === 'id' ? 'Pendaftaran gagal. Silakan coba lagi.' : 'Registration failed. Please try again.'));
      }
    } catch (err) {
      setError(currentLang === 'id' ? 'Kesalahan jaringan. Silakan periksa koneksi internet Anda.' : 'Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToHome = () => {
    if (onNavigate) {
      onNavigate('homepage');
    } else {
      window.location.href = '/';
    }
  };

  const handleSignIn = () => {
    if (formData.email) {
      try {
        localStorage.setItem('login_prefill_email', formData.email.toLowerCase().trim());
      } catch (_) {}
    }
    if (onNavigate) {
      onNavigate('login');
    } else {
      window.location.href = '/login';
    }
  };

  return (
    <div className="register-page">
      {/* Animated Floating Light Orbs (Luminous & Light Outside Canvas) */}
      <div className="register-ambient-light" aria-hidden="true">
        <div className="register-orb register-orb-1" />
        <div className="register-orb register-orb-2" />
        <div className="register-orb register-orb-3" />
      </div>

      {/* Floating Top Bar: Navigation & Language */}
      <header className="register-top-nav">
        <button
          type="button"
          className="register-nav-back-pill"
          onClick={handleBackToHome}
          aria-label={copy.backToHome}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          <span>{copy.backToHome}</span>
        </button>

        <div className="register-lang-pill">
          <button
            type="button"
            className={`register-lang-choice ${currentLang === 'id' ? 'active' : ''}`}
            onClick={() => setCurrentLang('id')}
          >
            ID
          </button>
          <span className="register-lang-sep" aria-hidden="true">|</span>
          <button
            type="button"
            className={`register-lang-choice ${currentLang === 'en' ? 'active' : ''}`}
            onClick={() => setCurrentLang('en')}
          >
            EN
          </button>
        </div>
      </header>

      {/* Floating Toast Notification */}
      {error && (
        <div className="register-toast-alert" role="alert">
          <div className="register-toast-alert-icon">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <div className="register-toast-alert-content">
            <strong>{copy.errorTitle}</strong>
            <p>{error}</p>
          </div>
          <button
            type="button"
            className="register-toast-alert-close"
            onClick={() => setError('')}
            aria-label="Close notification"
          >
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      {/* Main Dual-Panel Institutional Card (Royal Purple & Gold Side-by-Side) */}
      <main className="register-main-wrapper">
        <div className="register-card-shell">
          {success ? (
            /* Success State */
            <div className="register-success-container">
              <div className="register-success-badge-shell">
                <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline strokeLinecap="round" strokeLinejoin="round" points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>

              <span className="register-success-kicker">{copy.successKicker}</span>
              <h1 className="register-success-heading">{copy.successTitle}</h1>
              <p className="register-success-paragraph">{copy.successMessage}</p>

              <div className="register-success-summary-box">
                <div className="register-summary-row">
                  <span className="register-summary-icon">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </span>
                  <div className="register-summary-detail">
                    <span className="register-summary-label">{currentLang === 'id' ? 'Nama Orang Tua' : 'Parent Name'}</span>
                    <strong>{formData.fullName}</strong>
                  </div>
                </div>

                <div className="register-summary-row">
                  <span className="register-summary-icon">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </span>
                  <div className="register-summary-detail">
                    <span className="register-summary-label">{currentLang === 'id' ? 'Email Akun Portal' : 'Portal Email'}</span>
                    <strong>{formData.email}</strong>
                  </div>
                </div>

                <div className="register-summary-note">
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  <span>
                    {currentLang === 'id'
                      ? 'Setelah masuk, klik menu "Daftarkan Anak Baru" untuk memilih jenjang (PG, TK, SD, SMP, SMA). Anda dapat mendaftarkan lebih dari satu anak.'
                      : 'After signing in, click "Register New Child" to choose education levels (PG, TK, SD, SMP, SMA). You can enroll more than one child.'}
                  </span>
                </div>
              </div>

              <div className="register-success-cta-group">
                <button
                  type="button"
                  className="register-cta-secondary"
                  onClick={handleBackToHome}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                  <span>{copy.backToHome}</span>
                </button>
                <button
                  type="button"
                  className="register-cta-primary"
                  onClick={handleSignIn}
                >
                  <span>{copy.signInButton}</span>
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            /* Side-by-Side: Royal Purple & Gold Showcase + Crisp White Form */
            <>
              {/* LEFT PANEL: Royal Purple & Gold Institutional Showcase */}
              <aside className="register-royal-showcase">
                <div className="register-royal-glow" aria-hidden="true" />

                <div className="register-royal-content">
                  {/* Brand Lockup */}
                  <div className="register-royal-brand">
                    <div className="register-royal-logo-shield">
                      <img src={logoPHSchool} alt="Permata Harapan School" className="register-royal-logo-img" />
                    </div>
                    <div className="register-royal-brand-texts">
                      <span className="register-royal-school-title">Permata Harapan</span>
                      <span className="register-royal-school-subtitle">Preschool • Primary • Secondary</span>
                    </div>
                  </div>

                  {/* Gold Kicker Badge */}
                  <div className="register-gold-badge">
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                    <span>{copy.showcaseBadge}</span>
                  </div>

                  {/* Main Title & Subtitle */}
                  <h2 className="register-royal-heading">{copy.showcaseHeading}</h2>
                  <p className="register-royal-desc">{copy.showcaseDesc}</p>

                  {/* Available Education Levels (Clean Glass & Gold Badges) */}
                  <div className="register-royal-levels-block">
                    <span className="register-royal-block-label">{copy.levelsTitle}</span>
                    <div className="register-royal-levels-grid">
                      {copy.levels.map((lvl) => (
                        <div key={lvl.code} className="register-royal-level-pill">
                          <span className="register-gold-level-code">{lvl.code}</span>
                          <span className="register-royal-level-name">{lvl.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Concise Key Benefits List */}
                  <div className="register-royal-perks-block">
                    {copy.perks.map((perk, idx) => (
                      <div key={idx} className="register-royal-perk-item">
                        <span className="register-gold-perk-check">
                          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </span>
                        <span>{perk}</span>
                      </div>
                    ))}
                  </div>

                  {/* Security & Official Trust Badge */}
                  <div className="register-royal-trust-bar">
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                    <span>{copy.trustText}</span>
                  </div>
                </div>
              </aside>

              {/* RIGHT PANEL: Crisp White Form with Royal & Gold Accents */}
              <section className="register-royal-form-panel">
                <div className="register-royal-form-content">
                  {/* Form Header */}
                  <div className="register-royal-form-header">
                    <span className="register-royal-kicker-tag">{copy.kicker}</span>
                    <h1 className="register-royal-form-title">{copy.title}</h1>
                    <p className="register-royal-form-subtitle">{copy.subtitle}</p>
                  </div>

                  {/* Form Element */}
                  <form className="register-form-element" onSubmit={handleSubmit}>
                    <div className="register-fields-stack">
                      {/* Parent Full Name */}
                      <div className="register-field-unit">
                        <label className="register-field-label" htmlFor="fullName">
                          <span>{copy.fullNameLabel}</span>
                          <span className="register-field-req">*</span>
                        </label>
                        <div className="register-input-shell">
                          <span className="register-field-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                              <circle cx="12" cy="7" r="4" />
                            </svg>
                          </span>
                          <input
                            type="text"
                            id="fullName"
                            name="fullName"
                            className="register-field-input"
                            value={formData.fullName}
                            onChange={handleInputChange}
                            placeholder={copy.fullNamePlaceholder}
                            required
                            disabled={isSubmitting}
                            autoComplete="name"
                          />
                        </div>
                      </div>

                      {/* Email Address */}
                      <div className="register-field-unit">
                        <label className="register-field-label" htmlFor="email">
                          <span>{copy.emailLabel}</span>
                          <span className="register-field-req">*</span>
                        </label>
                        <div className="register-input-shell">
                          <span className="register-field-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                              <polyline points="22,6 12,13 2,6" />
                            </svg>
                          </span>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            className="register-field-input"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder={copy.emailPlaceholder}
                            required
                            disabled={isSubmitting}
                            autoComplete="email"
                          />
                        </div>
                      </div>

                      {/* Phone / WhatsApp */}
                      <div className="register-field-unit">
                        <label className="register-field-label" htmlFor="phone">
                          <span>{copy.phoneLabel}</span>
                          <span className="register-field-req">*</span>
                        </label>
                        <div className="register-input-shell">
                          <span className="register-field-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                            </svg>
                          </span>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            className="register-field-input"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder={copy.phonePlaceholder}
                            maxLength="13"
                            required
                            disabled={isSubmitting}
                            pattern="[0-9]*"
                            inputMode="numeric"
                            autoComplete="tel"
                          />
                        </div>
                      </div>

                      {/* Password & Confirm Password (2 Columns on Desktop) */}
                      <div className="register-fields-split-row">
                        {/* Password */}
                        <div className="register-field-unit">
                          <label className="register-field-label" htmlFor="password">
                            <span>{copy.passwordLabel}</span>
                            <span className="register-field-req">*</span>
                          </label>
                          <div className="register-input-shell has-toggle">
                            <span className="register-field-icon" aria-hidden="true">
                              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                              </svg>
                            </span>
                            <input
                              type={showPassword ? 'text' : 'password'}
                              id="password"
                              name="password"
                              className="register-field-input"
                              value={formData.password}
                              onChange={handleInputChange}
                              placeholder={copy.passwordPlaceholder}
                              required
                              disabled={isSubmitting}
                              minLength="8"
                              autoComplete="new-password"
                            />
                            <button
                              type="button"
                              className="register-toggle-pwd-btn"
                              onClick={() => setShowPassword(!showPassword)}
                              tabIndex="-1"
                              aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                              {showPassword ? (
                                <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                  <line x1="1" y1="1" x2="23" y2="23" />
                                </svg>
                              ) : (
                                <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                  <circle cx="12" cy="12" r="3" />
                                </svg>
                              )}
                            </button>
                          </div>
                        </div>

                        {/* Confirm Password */}
                        <div className="register-field-unit">
                          <label className="register-field-label" htmlFor="confirmPassword">
                            <span>{copy.confirmPasswordLabel}</span>
                            <span className="register-field-req">*</span>
                          </label>
                          <div className="register-input-shell has-toggle">
                            <span className="register-field-icon" aria-hidden="true">
                              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                              </svg>
                            </span>
                            <input
                              type={showConfirmPassword ? 'text' : 'password'}
                              id="confirmPassword"
                              name="confirmPassword"
                              className="register-field-input"
                              value={formData.confirmPassword}
                              onChange={handleInputChange}
                              placeholder={copy.confirmPasswordPlaceholder}
                              required
                              disabled={isSubmitting}
                              minLength="8"
                              autoComplete="new-password"
                            />
                            <button
                              type="button"
                              className="register-toggle-pwd-btn"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              tabIndex="-1"
                              aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                            >
                              {showConfirmPassword ? (
                                <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                  <line x1="1" y1="1" x2="23" y2="23" />
                                </svg>
                              ) : (
                                <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                  <circle cx="12" cy="12" r="3" />
                                </svg>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <span className="register-password-guideline">{copy.passwordHint}</span>
                    </div>

                    {/* Submit CTA (Royal Purple with Warm Gold Accent) */}
                    <button
                      type="submit"
                      className="register-cta-submit-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="register-submit-spinner-ring" aria-hidden="true" />
                          <span>{copy.registeringButton}</span>
                        </>
                      ) : (
                        <>
                          <span>{copy.registerButton}</span>
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="5" y1="12" x2="19" y2="12" />
                            <polyline points="12 5 19 12 12 19" />
                          </svg>
                        </>
                      )}
                    </button>

                    {/* Footer Portal Link */}
                    <div className="register-footer-prompt">
                      <span>{copy.loginPrompt}</span>
                      <button
                        type="button"
                        className="register-link-to-login"
                        onClick={handleSignIn}
                      >
                        {copy.loginLink}
                      </button>
                    </div>
                  </form>
                </div>
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default RegisterPage;
